/**
 * Shared bills (migration 006; docs/proposal-partial-facturas.md Part 1): the capture question "¿Quién pagó la cuenta?", asked
 * before a ticket goes up — on the camera, the picker, the share sheet and the desktop drop — and on Por revisar when it was not
 * answered. A shared bill is invoiced for the employee's part only: a card part is backed by the voucher, a cash part is typed.
 */
import { useEffect, useRef, useState } from 'react';
import { call } from '../api/client.ts';
import type { SplitKind } from '../api/types.ts';
import { Button, Icon, Sheet } from '../theme/ui.tsx';

export interface SplitAnswer { split: SplitKind; payment?: 'card' | 'cash'; share?: string }
export const SPLIT_HINT = 'Si comparten la cuenta, pide que la dividan: un ticket por persona se factura sin problema.';
const OPTIONS: { v: SplitKind; label: string }[] = [
  { v: 'whole', label: 'Yo, toda' },
  { v: 'coworkers', label: 'La compartí con compañeros de trabajo' },
  { v: 'others', label: 'La compartí con otras personas' },
];
export const isSharedKind = (k: SplitKind | null | undefined): k is 'coworkers' | 'others' => k === 'coworkers' || k === 'others';
const shareNumber = (s: string | undefined) => Number(String(s ?? '').replace(/[^0-9.]/g, ''));
/** Answered enough to upload: whole; or shared with a card (the voucher comes next); or shared in cash with the part typed. */
export function splitReady(a: SplitAnswer | null): boolean {
  if (!a) return false;
  if (a.split === 'whole') return true;
  if (a.payment === 'card') return true;
  return a.payment === 'cash' && shareNumber(a.share) > 0;
}
/** The answer as the upload's text parts (receipts.upload readSplit). */
export function appendSplit(fd: FormData, a: SplitAnswer, voucher?: File): FormData {
  fd.append('split', a.split);
  if (isSharedKind(a.split)) {
    fd.append('payment', a.payment!);
    if (a.payment === 'cash') fd.append('shareAmount', String(shareNumber(a.share)));
    if (a.payment === 'card' && voucher) fd.append('voucher', voucher, voucher.name);
  }
  return fd;
}

const isXmlOrZip = (f: File) => /\.(xml|zip)$/i.test(f.name) || /xml|zip/i.test(f.type);
/** The files are one ticket — its photos (one receipt, n pages) or a single PDF — the only uploads the question applies to. */
export function isSingleTicket(files: File[]): boolean {
  if (!files.length || files.some(isXmlOrZip)) return false;
  const pdfs = files.filter((f) => /pdf/i.test(f.type) || /\.pdf$/i.test(f.name));
  return pdfs.length === 0 || (pdfs.length === 1 && files.length === 1);
}
/** Any file the question applies to (a photo or a PDF): a mixed batch goes up unanswered and Por revisar asks per receipt. */
export const needsQuestion = (files: File[]) => files.some((f) => !isXmlOrZip(f));

let cached: Promise<boolean> | undefined;
/** PARTIAL_FACTURAS_ENABLED as /api/me reports it — asked once per page load. */
export function useSharedBills(): boolean | undefined {
  const [on, setOn] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    cached ??= call('me.get').then((m) => !!m.features?.sharedBills).catch(() => false);
    void cached.then((v) => { if (alive) setOn(v); });
    return () => { alive = false; };
  }, []);
  return on;
}

/** The question itself; `dark` on the capture screen. Asks how the part was paid once the bill is shared, and the amount for cash. */
export function SplitQuestion({ value, onChange, dark, hint = true }: { value: SplitAnswer | null; onChange: (a: SplitAnswer | null) => void; dark?: boolean; hint?: boolean }) {
  const shared = isSharedKind(value?.split);
  return (
    <div className={`split-q ${dark ? 'split-q--dark' : ''}`}>
      {hint && <div className="split-q__hint">{SPLIT_HINT}</div>}
      <div className="label" style={{ marginTop: hint ? 6 : 0 }}>¿Quién pagó la cuenta?</div>
      {OPTIONS.map((o) => (
        <button key={o.v} type="button" className={`split-q__opt ${value?.split === o.v ? 'split-q__opt--on' : ''}`} aria-pressed={value?.split === o.v}
          onClick={() => onChange(o.v === 'whole' ? { split: 'whole' } : { split: o.v, payment: value?.payment, share: value?.share })}>
          <span>{o.label}</span>{value?.split === o.v && <Icon.Check size={17} width={2.2} />}
        </button>
      ))}
      {shared && (
        <>
          <div className="label" style={{ marginTop: 8 }}>¿Cómo pagaste tu parte?</div>
          <div className="chips" style={{ flexWrap: 'nowrap' }}>
            {(['card', 'cash'] as const).map((p) => (
              <button key={p} type="button" className={`chip chip--grow split-q__chip ${value?.payment === p ? 'chip--on' : ''}`} aria-pressed={value?.payment === p}
                onClick={() => onChange({ ...value!, payment: p })}>{p === 'card' ? 'Tarjeta' : 'Efectivo'}</button>
            ))}
          </div>
          {value?.payment === 'card' && <div className="split-q__hint">Después del ticket te pedimos el voucher de la terminal con tu pago.</div>}
          {value?.payment === 'cash' && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="split-q__hint">Tu parte, en pesos (sin propina)</span>
              <input className="input input--mono" type="number" inputMode="decimal" step="0.01" min="0" placeholder="0.00" value={value.share ?? ''} onChange={(e) => onChange({ ...value, share: e.target.value })} />
            </label>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The question as a sheet for an upload that did not go through the camera (desktop drop, Android share): the files are the
 * ticket; a card part asks for the voucher here — one of the files already chosen, or a new one.
 */
export function SplitSheet({ files, onCancel, onSend }: { files: File[]; onCancel: () => void; onSend: (fd: FormData) => Promise<void> | void }) {
  const [a, setA] = useState<SplitAnswer | null>(null);
  const [voucher, setVoucher] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const pick = useRef<HTMLInputElement>(null);
  const card = isSharedKind(a?.split) && a?.payment === 'card';
  // a card part: one of the chosen files may already be the voucher (two photos shared together); the rest is the ticket
  const ticket = card && voucher && files.includes(voucher) ? files.filter((f) => f !== voucher) : files;
  const ok = splitReady(a) && (!card || (!!voucher && ticket.length > 0)) && (!isSharedKind(a?.split) || isSingleTicket(ticket));
  const send = async () => {
    if (!ok || !a) return; setBusy(true);
    const fd = new FormData(); for (const f of ticket) fd.append('files', f, f.name);
    try { await onSend(appendSplit(fd, a, card ? voucher ?? undefined : undefined)); } finally { setBusy(false); }
  };
  return (
    <Sheet onClose={onCancel}>
      <div className="h2">Antes de subirlo</div>
      <SplitQuestion value={a} onChange={setA} />
      {isSharedKind(a?.split) && !isSingleTicket(ticket) && <div className="sub" style={{ color: 'var(--red)' }}>Una cuenta compartida es un solo ticket: sus fotos o un PDF.</div>}
      {card && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="label">Tu voucher</div>
          {files.length > 1 && files.filter((f) => !/\.(xml|zip)$/i.test(f.name)).map((f) => (
            <button key={f.name} type="button" className={`split-q__opt ${voucher === f ? 'split-q__opt--on' : ''}`} onClick={() => setVoucher(voucher === f ? null : f)}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span><span className="sub">{voucher === f ? 'es el voucher' : 'este es el voucher'}</span>
            </button>
          ))}
          <label className="btn btn--secondary btn--sm" style={{ cursor: 'pointer' }}>
            <Icon.Upload size={16} width={1.8} /> {voucher && !files.includes(voucher) ? voucher.name : 'Elegir el voucher (foto o PDF)'}
            <input ref={pick} type="file" accept="image/jpeg,image/png,application/pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) setVoucher(f); e.target.value = ''; }} />
          </label>
        </div>
      )}
      <Button onClick={send} busy={busy} disabled={!ok}>Subir</Button>
      <Button variant="ghost" onClick={onCancel}>Cancelar</Button>
    </Sheet>
  );
}
